"use client";

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  X,
  Sparkles,
} from "lucide-react";

export type DialogVariant = "danger" | "warning" | "info" | "success" | "primary";

export interface ConfirmOptions {
  title?: string;
  message: string | ReactNode;
  details?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface AlertOptions {
  title?: string;
  message: string | ReactNode;
  details?: string;
  confirmText?: string;
  variant?: DialogVariant;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface PromptOptions {
  title?: string;
  message: string | ReactNode;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  inputType?: "text" | "password" | "number" | "email";
}

interface DialogState {
  type: "confirm" | "alert" | "prompt";
  title?: string;
  message: string | ReactNode;
  details?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  icon?: React.ComponentType<{ className?: string }>;
  promptValue?: string;
  promptPlaceholder?: string;
  promptInputType?: string;
  resolve: (value: any) => void;
}

interface DialogContextType {
  confirm: (options: string | ConfirmOptions) => Promise<boolean>;
  alert: (options: string | AlertOptions) => Promise<void>;
  prompt: (options: string | PromptOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | null>(null);

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return ctx;
}

// --------------------------------------------------------------------------
// HTML5 Dialog Provider & Imperative Dialog Box (Confirm / Alert / Prompt)
// --------------------------------------------------------------------------
export function DialogProvider({ children }: { children: ReactNode }) {
  const [activeDialog, setActiveDialog] = useState<DialogState | null>(null);
  const [promptInput, setPromptInput] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const promptInputRef = useRef<HTMLInputElement>(null);

  // Sync native HTML5 <dialog> showModal / close
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    if (activeDialog) {
      if (!el.open) {
        try {
          el.showModal();
        } catch {
          el.setAttribute("open", "true");
        }
      }
      if (activeDialog.type === "prompt") {
        setTimeout(() => {
          promptInputRef.current?.focus();
          promptInputRef.current?.select();
        }, 50);
      }
    } else {
      if (el.open) {
        try {
          el.close();
        } catch {
          el.removeAttribute("open");
        }
      }
    }
  }, [activeDialog]);

  const confirm = useCallback((opts: string | ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const options: ConfirmOptions = typeof opts === "string" ? { message: opts } : opts;
      setActiveDialog({
        type: "confirm",
        title: options.title || "Confirm Action",
        message: options.message,
        details: options.details,
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        variant: options.variant || "danger",
        icon: options.icon,
        resolve,
      });
    });
  }, []);

  const alert = useCallback((opts: string | AlertOptions): Promise<void> => {
    return new Promise<void>((resolve) => {
      const options: AlertOptions = typeof opts === "string" ? { message: opts } : opts;
      setActiveDialog({
        type: "alert",
        title: options.title || "Notice",
        message: options.message,
        details: options.details,
        confirmText: options.confirmText || "OK",
        variant: options.variant || "info",
        icon: options.icon,
        resolve: () => resolve(),
      });
    });
  }, []);

  const prompt = useCallback((opts: string | PromptOptions): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      const options: PromptOptions = typeof opts === "string" ? { message: opts } : opts;
      setPromptInput(options.defaultValue || "");
      setActiveDialog({
        type: "prompt",
        title: options.title || "Input Required",
        message: options.message,
        confirmText: options.confirmText || "Submit",
        cancelText: options.cancelText || "Cancel",
        promptPlaceholder: options.placeholder || "",
        promptInputType: options.inputType || "text",
        variant: "primary",
        resolve,
      });
    });
  }, []);

  const handleConfirmAction = () => {
    if (!activeDialog) return;
    if (activeDialog.type === "prompt") {
      activeDialog.resolve(promptInput);
    } else {
      activeDialog.resolve(true);
    }
    setActiveDialog(null);
  };

  const handleCancelAction = () => {
    if (!activeDialog) return;
    if (activeDialog.type === "prompt") {
      activeDialog.resolve(null);
    } else if (activeDialog.type === "confirm") {
      activeDialog.resolve(false);
    } else {
      activeDialog.resolve(undefined);
    }
    setActiveDialog(null);
  };

  const handleNativeCancel = (e: React.SyntheticEvent<HTMLDialogElement, Event>) => {
    e.preventDefault();
    handleCancelAction();
  };

  const renderIcon = () => {
    if (!activeDialog) return null;
    if (activeDialog.icon) {
      const CustomIcon = activeDialog.icon;
      return <CustomIcon className="w-5 h-5 shrink-0" />;
    }

    const v = activeDialog.variant || "info";
    switch (v) {
      case "danger":
        return <XCircle className="w-5 h-5 text-status-danger shrink-0" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-status-warning shrink-0" />;
      case "success":
        return <CheckCircle2 className="w-5 h-5 text-status-success shrink-0" />;
      case "primary":
        return <Sparkles className="w-5 h-5 text-themed-accentPrimary shrink-0" />;
      case "info":
      default:
        return <Info className="w-5 h-5 text-status-info shrink-0" />;
    }
  };

  const getVariantStyles = () => {
    const v = activeDialog?.variant || "info";
    switch (v) {
      case "danger":
        return {
          iconBox: "bg-status-danger-bg border-status-danger-border text-status-danger",
          confirmBtn:
            "bg-status-danger hover:opacity-90 text-white shadow-lg focus:ring-status-danger/40",
          border: "border-status-danger-border",
        };
      case "warning":
        return {
          iconBox: "bg-status-warning-bg border-status-warning-border text-status-warning",
          confirmBtn:
            "bg-status-warning hover:opacity-90 text-themed-textPrimary shadow-lg focus:ring-status-warning/40",
          border: "border-status-warning-border",
        };
      case "success":
        return {
          iconBox: "bg-status-success-bg border-status-success-border text-status-success",
          confirmBtn:
            "bg-status-success hover:opacity-90 text-themed-textPrimary shadow-lg focus:ring-status-success/40",
          border: "border-status-success-border",
        };
      case "primary":
        return {
          iconBox: "bg-themed-accentSubtle border-themed-borderAccent text-themed-accentPrimary",
          confirmBtn:
            "bg-gradient-to-r from-themed-accentPrimary to-themed-accentSecondary text-themed-accentText font-bold shadow-lg shadow-[var(--accent-glow)] focus:ring-themed-accentPrimary/40",
          border: "border-themed-borderAccent",
        };
      case "info":
      default:
        return {
          iconBox: "bg-status-info-bg border-status-info-border text-status-info",
          confirmBtn:
            "bg-status-info hover:opacity-90 text-themed-textPrimary shadow-lg focus:ring-status-info/40",
          border: "border-status-info-border",
        };
    }
  };

  const vStyles = getVariantStyles();

  return (
    <DialogContext.Provider value={{ confirm, alert, prompt }}>
      {children}

      {/* Reusable HTML5 <dialog> element for Confirmation / Alert / Prompt */}
      <dialog
        ref={dialogRef}
        onCancel={handleNativeCancel}
        className="fixed inset-0 z-[100] m-auto p-0 bg-transparent backdrop:bg-surface-overlay backdrop:backdrop-blur-md outline-none max-w-lg w-[calc(100%-2rem)] open:animate-in open:fade-in open:zoom-in-95 duration-150"
      >
        {activeDialog && (
          <div
            className={`bg-surface-card border ${vStyles.border} rounded-2xl shadow-2xl overflow-hidden p-6 text-themed-textPrimary flex flex-col gap-4 max-h-[90vh]`}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2.5 rounded-xl border ${vStyles.iconBox} flex items-center justify-center`}
                >
                  {renderIcon()}
                </div>
                <div>
                  <h3 className="text-base font-bold text-themed-textPrimary leading-snug">
                    {activeDialog.title}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelAction}
                className="text-themed-textMuted hover:text-themed-textPrimary p-1.5 rounded-lg hover:bg-surface-cardHover transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Message Body */}
            <div className="text-xs text-themed-textSecondary leading-relaxed space-y-2">
              {typeof activeDialog.message === "string" ? (
                <p className="whitespace-pre-wrap">{activeDialog.message}</p>
              ) : (
                activeDialog.message
              )}

              {activeDialog.details && (
                <div className="p-2.5 rounded-xl bg-surface-root border border-themed-border text-[11px] font-mono text-themed-textMuted overflow-x-auto max-h-32">
                  {activeDialog.details}
                </div>
              )}

              {activeDialog.type === "prompt" && (
                <div className="pt-2">
                  <input
                    ref={promptInputRef}
                    type={activeDialog.promptInputType || "text"}
                    value={promptInput}
                    placeholder={activeDialog.promptPlaceholder}
                    onChange={(e) => setPromptInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleConfirmAction();
                      }
                    }}
                    className="w-full bg-surface-input border border-themed-border rounded-xl px-3 py-2 text-xs text-themed-textPrimary placeholder:text-themed-textMuted focus:outline-none focus:border-[var(--accent-primary)]"
                  />
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-themed-borderSubtle">
              {activeDialog.type !== "alert" && (
                <button
                  type="button"
                  onClick={handleCancelAction}
                  className="px-4 py-2 rounded-xl bg-surface-muted hover:bg-surface-cardHover text-themed-textSecondary hover:text-themed-textPrimary border border-themed-border text-xs font-semibold transition"
                >
                  {activeDialog.cancelText || "Cancel"}
                </button>
              )}

              <button
                type="button"
                autoFocus
                onClick={handleConfirmAction}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition focus:outline-none focus:ring-2 ${vStyles.confirmBtn}`}
              >
                {activeDialog.confirmText || "Confirm"}
              </button>
            </div>
          </div>
        )}
      </dialog>
    </DialogContext.Provider>
  );
}

// --------------------------------------------------------------------------
// Declarative HTML5 <dialog> Component for custom modals
// --------------------------------------------------------------------------
export interface Html5DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  className?: string;
  closeOnClickOutside?: boolean;
}

const MAX_WIDTH_CLASSES: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
};

export function Html5Dialog({
  isOpen,
  onClose,
  title,
  description,
  icon: Icon,
  children,
  footer,
  maxWidth = "2xl",
  className = "",
  closeOnClickOutside = true,
}: Html5DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    if (isOpen) {
      if (!el.open) {
        try {
          el.showModal();
        } catch {
          el.setAttribute("open", "true");
        }
      }
    } else {
      if (el.open) {
        try {
          el.close();
        } catch {
          el.removeAttribute("open");
        }
      }
    }
  }, [isOpen]);

  const handleNativeCancel = (e: React.SyntheticEvent<HTMLDialogElement, Event>) => {
    e.preventDefault();
    onClose();
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (!closeOnClickOutside) return;
    const rect = dialogRef.current?.getBoundingClientRect();
    if (rect) {
      const isOutside =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;
      if (isOutside) {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  const maxWidthClass = MAX_WIDTH_CLASSES[maxWidth] || "max-w-2xl";

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleNativeCancel}
      onClick={handleDialogClick}
      className={`fixed inset-0 z-50 m-auto p-0 bg-transparent backdrop:bg-surface-overlay backdrop:backdrop-blur-md outline-none w-[calc(100%-2rem)] ${maxWidthClass} open:animate-in open:fade-in open:zoom-in-95 duration-150`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-surface-card border border-themed-border rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] text-themed-textPrimary ${className}`}
      >
        {/* Dialog Header */}
        {(title || Icon) && (
          <div className="p-6 border-b border-themed-borderSubtle flex items-center justify-between bg-surface-muted/40 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              {Icon && (
                <div className="p-2.5 rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] border border-[var(--accent-primary)]/20">
                  <Icon className="w-5 h-5" />
                </div>
              )}
              <div>
                {title && <h3 className="text-base font-bold text-themed-textPrimary">{title}</h3>}
                {description && <p className="text-xs text-themed-textSecondary mt-0.5">{description}</p>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-xl text-themed-textMuted hover:text-themed-textPrimary hover:bg-surface-cardHover transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Dialog Content */}
        <div className="p-6 overflow-y-auto flex-1">{children}</div>

        {/* Dialog Footer */}
        {footer && (
          <div className="p-4 px-6 border-t border-themed-borderSubtle bg-surface-muted/20 flex items-center justify-end gap-3 sticky bottom-0 z-10">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
